import { useBooleanState } from "~/common/hooks/useBooleanState";
import type { GroupedQuoteLines, RenderCboxFn } from "~/features/orders/common/components/v2/QuoteTable/types";
import { splitAlternatives } from "~/features/orders/common/components/v2/QuoteTable/utils";
import { ProductHeader } from "./ProductHeader";
import { useAbsoluteLineAlignment } from "../common/components/v2/QuoteTable/useLineNoOffsetCalculation";
import { QuotePartsList } from "./QuotePartsList";
import { AccordionDetails, AccordionSummary, Stack, Typography } from "~/common/mui";
import {
  BorderLeftWrapper,
  ContainerStack,
  OrderCardAccordion,
  OrderCardAccordionSummary,
  OrderLineServiceCase,
  QuoteHeaderSummaryContainer,
} from "../common/components/v2/OrderAndQuoteSharedStyles";
import QuoteLineHeaderSection from "./QuoteLineHeaderSection";
import { AccordionSummaryToggle } from "../common/components/v2/AccordionSummaryToggle";
import { useTranslation } from "react-i18next";

interface AdvancedQuoteRowProps extends GroupedQuoteLines {
  renderCheckbox: RenderCboxFn;
}

/**
 * AdvancedQuoteRow - Displays a quote row with optional alternatives.
 *
 * Implements line number alignment using absolute positioning to ensure cross-browser
 * compatibility, specifically addressing Safari issues with CSS Grid row subgrid.
 */
export const AdvancedQuoteRow = (props: AdvancedQuoteRowProps) => {
  const { t } = useTranslation();
  const { mainQuoteLine, sameProductAlternatives, differentProductAlternatives } = splitAlternatives(props);
  const { val: isAlternativeExpanded, toggle: toggleAlternative } = useBooleanState(false);
  const { val: isAlternativeProductsExpanded, toggle: toggleAlternativeProducts } = useBooleanState(false);
  const renderCheckbox = props.renderCheckbox;

  const { registerTarget } = useAbsoluteLineAlignment();
  const content = (
    <>
      {/* Product Header */}
      <ProductHeader quoteLine={mainQuoteLine} />
      {/* Main Quote Line Section */}
      <QuotePartsList ref={registerTarget(mainQuoteLine.lineNo)} quoteLine={mainQuoteLine} forceRender />
    </>
  );

  return (
    <>
      {/* Main line number */}

      <ContainerStack>
        <QuoteLineHeaderSection
          quoteLine={mainQuoteLine}
          includeQuoteLine={mainQuoteLine.parts.length < 2}
          renderCheckbox={renderCheckbox}
        />
        <QuoteHeaderSummaryContainer>
          {sameProductAlternatives.length > 0 || differentProductAlternatives.length > 0 ? (
            <BorderLeftWrapper>{content}</BorderLeftWrapper>
          ) : (
            content
          )}
        </QuoteHeaderSummaryContainer>

        {/* Alternative Quote Lines (same product) */}

        <OrderLineServiceCase expanded={isAlternativeExpanded} onChange={toggleAlternative}>
          <AccordionSummary sx={{ bgcolor: "surfaceContainer.low" }}>
            <Typography component={"b"} variant="bodyLarge">
              {t("quoteList.alternative.accordionSummary.title")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Stack sx={{ bgcolor: "surfaceContainer.low" }}>
              {isAlternativeExpanded &&
                sameProductAlternatives.map((quoteLine) => {
                  return (
                    <>
                      <QuotePartsList
                        key={quoteLine.lineNo}
                        ref={registerTarget(quoteLine.lineNo)}
                        quoteLine={quoteLine}
                        variant="alternative"
                        renderCheckbox={renderCheckbox}
                        showDivider={true}
                      />
                    </>
                  );
                })}
            </Stack>
          </AccordionDetails>
        </OrderLineServiceCase>

        {/* Alternative Products */}
        {differentProductAlternatives.length > 0 && (
          <OrderCardAccordion expanded={isAlternativeProductsExpanded} onChange={toggleAlternativeProducts}>
            <OrderCardAccordionSummary expandIcon={null} sx={{ px: 0 }}>
              <AccordionSummaryToggle
                open={isAlternativeProductsExpanded}
                showLabel={t("quoteList.alternativeProducts.accordionSummary.show")}
                hideLabel={t("quoteList.alternativeProducts.accordionSummary.hide")}
              />
            </OrderCardAccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {isAlternativeProductsExpanded &&
                differentProductAlternatives.map(({ quoteLines }) => (
                  <Stack gap={1}>
                    <QuoteHeaderSummaryContainer>
                      <BorderLeftWrapper variant={"alternative"}>
                        <ProductHeader quoteLine={quoteLines[0]} alternative />
                      </BorderLeftWrapper>
                      {quoteLines.map((quoteLine, index) => (
                        <QuotePartsList
                          key={quoteLine.lineNo}
                          ref={index > 0 ? registerTarget(quoteLine.lineNo) : undefined}
                          quoteLine={quoteLine}
                          variant="alternative"
                          renderCheckbox={renderCheckbox}
                        />
                      ))}
                    </QuoteHeaderSummaryContainer>
                  </Stack>
                ))}
            </AccordionDetails>
          </OrderCardAccordion>
        )}
      </ContainerStack>
    </>
  );
};
